import { Editor } from '@tinymce/tinymce-react';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8000/api';
const STORAGE_KEY = 'admin_token';

interface Props {
  value: string;
  onChange: (html: string) => void;
  siteContentId?: string;
  height?: number;
  disabled?: boolean;
}

export default function RichTextEditor({
  value,
  onChange,
  siteContentId,
  height = 500,
  disabled = false,
}: Props) {
  const imagesUploadHandler = async (blobInfo: {
    blob: () => Blob;
    filename: () => string;
  }): Promise<string> => {
    const formData = new FormData();
    formData.append('file', blobInfo.blob(), blobInfo.filename());
    if (siteContentId) {
      formData.append('site_content_id', siteContentId);
    }

    const token = localStorage.getItem(STORAGE_KEY);

    const response = await fetch(`${BASE_URL}/admin/cms/images`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        Accept: 'application/json',
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Image upload failed');
    }

    const data = await response.json();
    return data.location;
  };

  return (
    <Editor
      value={value}
      disabled={disabled}
      tinymceScriptSrc="/libs/tinymce/tinymce.min.js"
      init={{
        base_url: '/libs/tinymce',
        suffix: '.min',
        height,
        menubar: true,
        plugins: [
          'advlist',
          'autolink',
          'lists',
          'link',
          'image',
          'charmap',
          'anchor',
          'searchreplace',
          'visualblocks',
          'code',
          'fullscreen',
          'insertdatetime',
          'media',
          'table',
          'help',
          'wordcount',
        ],
        toolbar:
          'undo redo | blocks | bold italic underline strikethrough | ' +
          'alignleft aligncenter alignright alignjustify | ' +
          'bullist numlist outdent indent | removeformat | ' +
          'link image media table | code fullscreen | help',
        images_upload_handler: imagesUploadHandler,
        automatic_uploads: true,
        file_picker_types: 'image',
        content_style:
          'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }',
      }}
      onEditorChange={onChange}
    />
  );
}
